import {
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  FormEvent,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _unescape from 'lodash/unescape';
import { skipToken } from '@reduxjs/toolkit/query';

import PageLayout from 'layouts/PageLayout';
import { MassMessageRole, MassMessageSpace, ExtendedMassMessage } from 'models';
import { customFormat } from 'utils';
import { PortalsName } from 'utils/enums';

import Dialog from 'shared/components/Dialog';
import useCooperativeId from 'shared/hooks/useCooperativeId';
import useSelector from 'shared/hooks/useSelector';
import br2nl from 'shared/utils/br2nl';
import mapIds from 'shared/utils/mapIds';

import { massMessagesApi } from 'pages/mass-messages/api';
import { fileLoaderApi, EntityType } from 'features/file-loader';

import {
  communicationMessagesApi,
  FormValues,
} from 'services/communication-messages';

import { isAnySendingMethodSelected } from './helpers';
import Notification from './Notification';

import {
  Modal,
  MassMessageCreateFormDetails,
  PageLoader,
  Error,
  NoData,
} from 'components';

enum ValidationError {
  Required = 'Lisää puuttuva tieto',
}
interface MassMessageCreateFormProps {
  handleModalClose: (update?: boolean, mm?: ExtendedMassMessage) => void;
  method: 'create' | 'update';
  communicationMessageId: string | null;
}

const initialFormValues: FormValues = {
  description: '',
  floorAppMessageDue: null,
  portalMessageDue: null,
  selectedRoles: [],
  selectedSpaces: [],
  sendEmail: false,
  sendFloorApp: false,
  sendPortalMessage: false,
  sendSMS: false,
  subject: '',
};

const MassMessageCreateForm = ({
  handleModalClose,
  method,
  communicationMessageId,
}: MassMessageCreateFormProps) => {
  const history = useHistory();
  const location = useLocation();
  const coopId = useCooperativeId();
  const portal = useSelector((state) => state.settings.current_portal);

  const { documentsIds } = fileLoaderApi.useGetAttachmentsQuery(
    coopId && communicationMessageId && method === 'update'
      ? {
          cooperativeId: coopId,
          entityId: communicationMessageId,
          entityType: EntityType.MassMessage,
        }
      : skipToken,
    {
      selectFromResult: (res) => ({
        documentsIds: (res.data ?? [])
          .filter(({ CanDelete }) => CanDelete)
          .map(({ Id }) => Id),
      }),
    }
  );

  const [openNotification, setOpenNotification] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [documentsToBeDeleted, setDocumentsToBeDeleted] = useState<string[]>(
    []
  );

  const {
    data: initialFormData,
    isLoading,
    isFetching,
    isError,
  } = communicationMessagesApi.useGetInitialFormValueQuery(
    {
      communicationMessageId,
      coopId,
      method,
    },
    {
      skip: !!!coopId,
      refetchOnMountOrArgChange: true,
    }
  );
  const [saveMassMessageForm, { isLoading: isUploading }] =
    massMessagesApi.useSaveMassMessageMutation();

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
    initialValues: initialFormValues,
    validate: (values) => {
      let errors = {};

      if (!values.subject.trim()) {
        _set(errors, 'subject', ValidationError.Required);
        return errors;
      }

      if (!values.description.trim()) {
        _set(errors, 'description', ValidationError.Required);
        return errors;
      }

      // if (values.sendFloorApp) {
      //   if (!values.floorAppMessageDue) {
      //     _set(errors, 'floorAppMessageDue', ValidationError.Required);
      //     return errors;
      //   }
      // }

      if (!values.selectedRoles.length) {
        _set(errors, 'selectedRoles', ValidationError.Required);
        return errors;
      }

      if (
        !isAnySendingMethodSelected({
          sendEmail: values.sendEmail,
          sendFloorApp: values.sendFloorApp,
          sendPortalMessage: values.sendPortalMessage,
          sendSms: values.sendSMS,
        })
      ) {
        _set(errors, 'selectedSendingMethods', ValidationError.Required);
        return errors;
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!coopId) return;

      const body: Record<string, any> = {
        CooperativeId: coopId,
        Subject: values.subject,
        Description: values.description,
        SendSms: values.sendSMS,
        SendEmail: values.sendEmail,
        SendPortalMessage: values.sendPortalMessage,
        SendFloorApp: values.sendFloorApp,
        PortalMessageDue: customFormat(values.portalMessageDue, 'server'),
        FloorAppMessageDue: customFormat(values.floorAppMessageDue, 'server'),
        RecipientRoles: mapIds(values.selectedRoles),
        Spaces: mapIds(values.selectedSpaces),
      };

      if (method === 'update') {
        body.Id = communicationMessageId;
      }

      const res = await saveMassMessageForm({
        body,
        coopId,
        documentsToBeDeleted,
        files,
        variant: method,
      });

      const message = _get(res, 'data');

      if (message) {
        const matchedEntity = location.pathname.match(
          /mass-messages|constructors-mass-messages/
        );
        const entity =
          portal?.Name === PortalsName.Maintenance
            ? 'maintenance/mass-messages'
            : matchedEntity
            ? matchedEntity[0]
            : null;

        if (method === 'create') {
          history.push(
            `/${entity}/${(message as unknown as ExtendedMassMessage).Id}`
          );
        }

        handleModalClose(true);
      }
    },
  });

  const handleChangeCheckbox = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;

      const prevFormState = formik.values;

      if (name === 'sendSMS') {
        if (
          checked &&
          (files.length || documentsIds.length !== documentsToBeDeleted.length)
        ) {
          handleOpenNotification();
          return;
        }

        formik.setValues({
          ...prevFormState,
          sendEmail: false,
          sendFloorApp: false,
          sendPortalMessage: false,
          sendSMS: checked,
        });
        return;
      }

      if (
        name === 'sendEmail' ||
        name === 'sendFloorApp' ||
        name === 'sendPortalMessage'
      ) {
        formik.setValues({
          ...prevFormState,
          [name]: checked,
          sendSMS: false,
        });
        return;
      }

      formik.setFieldValue(name, checked);
    },
    [formik, documentsIds, documentsToBeDeleted, files]
  );

  const handleChangeDate = useCallback(
    (newDate: Date | null, name?: string) => {
      if (name) {
        formik.setFieldValue(name, newDate);
      }
    },
    [formik]
  );

  const handleChangeTextField = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      formik.setFieldValue(name, value);
    },
    [formik]
  );

  const handleChangeSpaces = useCallback(
    (newSelectedSpaces: MassMessageSpace[], checked: boolean) => {
      const { values } = formik;
      const newSpaces: MassMessageSpace[] = checked
        ? [...values.selectedSpaces, ...newSelectedSpaces]
        : formik.values.selectedSpaces.filter(
            (space) =>
              !newSelectedSpaces.some(
                (newSelectedSpace) => newSelectedSpace.Id === space.Id
              )
          );

      formik.setFieldValue('selectedSpaces', newSpaces);
    },
    [formik]
  );

  const handleChangeRoles = useCallback(
    (newSelectedRoles: MassMessageRole[], checked: boolean) => {
      const { values } = formik;
      const newRoles: MassMessageRole[] = checked
        ? [...values.selectedRoles, ...newSelectedRoles]
        : formik.values.selectedRoles.filter(
            (role) =>
              !newSelectedRoles.some(
                (newSelectedRole) => newSelectedRole.Id === role.Id
              )
          );

      formik.setFieldValue('selectedRoles', newRoles);
    },
    [formik]
  );

  const handleInitErrors = useCallback(() => formik.setErrors({}), [formik]);

  const handleAddFiles = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
  }, []);

  const handleSubmitForm = useCallback(
    (event: FormEvent<HTMLFormElement>) => formik.handleSubmit(event),
    [formik]
  );

  const handleAddDocumentsToDeletionQueue = (documentId: string) => {
    setDocumentsToBeDeleted((prevState) => [...prevState, documentId]);
  };

  const handleCloseForm = useCallback(() => {
    handleModalClose();
  }, [handleModalClose]);

  const handleOpenNotification = () => {
    setOpenNotification(true);
  };

  const handleCloseNotification = () => {
    setOpenNotification(false);
  };

  const _clearAttachments = () => {
    if (documentsIds.length) {
      setDocumentsToBeDeleted(documentsIds);
    }

    setFiles([]);
  };

  const handleApply = () => {
    _clearAttachments();
    formik.setValues({
      ...formik.values,
      sendEmail: false,
      sendFloorApp: false,
      sendPortalMessage: false,
      sendSMS: true,
    });
    handleCloseNotification();
  };

  const handleReject = () => {
    formik.setFieldValue('sendSMS', false);
    handleCloseNotification();
  };

  const showLoader = isLoading || isFetching;
  const showDummyBlock = !formik.dirty || !!!initialFormData;

  useEffect(() => {
    if (initialFormData) {
      const { initialValues } = initialFormData;

      formik.setValues({
        description: br2nl(_unescape(initialValues.description)),
        floorAppMessageDue: initialValues.floorAppMessageDue,
        portalMessageDue: initialValues.portalMessageDue,
        selectedRoles: initialValues.selectedRoles,
        selectedSpaces: initialValues.selectedSpaces,
        sendEmail: initialValues.sendEmail,
        sendFloorApp: false, //initialValues.sendFloorApp,
        sendPortalMessage: initialValues.sendPortalMessage,
        sendSMS: initialValues.sendSMS,
        subject: _unescape(initialValues.subject),
      });
    }
  }, [initialFormData]);

  return (
    <>
      <Modal open fullWidth handleClose={handleModalClose} maxWidth="lg">
        {showLoader ? (
          <PageLoader />
        ) : isError ? (
          <Error />
        ) : showDummyBlock ? (
          <NoData />
        ) : (
          <MassMessageCreateFormDetails
            availableSendingMethods={
              initialFormData.initialValues.availableSendingMethods
            }
            buildingsList={initialFormData.buildings}
            communicationMessageId={communicationMessageId}
            documentsToBeDeleted={documentsToBeDeleted}
            files={files}
            errors={formik.errors}
            isUploading={isUploading}
            method={method}
            values={formik.values}
            onAddFiles={handleAddFiles}
            onAddDocumentToDeletionQueue={handleAddDocumentsToDeletionQueue}
            onChangeCheckbox={handleChangeCheckbox}
            onChangeDate={handleChangeDate}
            onChangeRoles={handleChangeRoles}
            onChangeSpaces={handleChangeSpaces}
            onChangeTextField={handleChangeTextField}
            onCloseForm={handleCloseForm}
            onInitFormErrors={handleInitErrors}
            onSubmit={handleSubmitForm}
            rolesEntities={initialFormData.rolesEntities}
          />
        )}
      </Modal>
      <PageLayout>
        <Dialog
          fullWidth
          maxWidth="sm"
          open={openNotification}
          onClose={handleCloseNotification}
        >
          <Notification onApply={handleApply} onReject={handleReject} />
        </Dialog>
      </PageLayout>
    </>
  );
};

export default MassMessageCreateForm;
